import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logoWhite from '../assets/logo-white.png';
import { useMediaQuery } from '../hooks/useMediaQuery';

/**
 * Fixed header from the approved design.
 *
 * - Transparent gradient over the home hero, solid once scrolled past 40px.
 * - Ten destinations would crowd the bar, so related pages sit behind four top-level
 *   entries and open in a small panel. Every page is still one or two clicks away and
 *   still has its own URL; nothing was removed from the site.
 * - Desktop nav from 1100px up; below that, the bordered hamburger toggle.
 * - The drawer lists everything flat under group headings, closes on Escape or route
 *   change, and returns focus to the toggle.
 */

export const DESKTOP_NAV_QUERY = '(min-width: 1100px)';

interface NavLeaf {
  to: string;
  label: string;
  hint?: string;
  end?: boolean;
}

interface NavGroup {
  label: string;
  children: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

const NAV_ITEMS: NavEntry[] = [
  { to: '/our-story', label: 'Our Story' },
  {
    label: 'Menu',
    children: [
      { to: '/menu/drinks', label: 'Drinks', hint: 'Cocktails, mocktails, coffee and tea' },
      { to: '/menu/food', label: 'Food', hint: 'Breakfast, light plates and desserts' },
    ],
  },
  {
    label: 'What’s On',
    children: [
      { to: '/experiences', label: 'Experiences', hint: 'Tastings, live sets and rituals' },
      { to: '/events', label: 'Events', hint: 'Our calendar and private hire' },
      { to: '/gallery', label: 'Gallery', hint: 'The rooms, the bar, the terrace' },
    ],
  },
  {
    label: 'Services',
    children: [
      { to: '/concierge', label: 'Concierge', hint: 'Requests handled by our team' },
      { to: '/laundry', label: 'Laundry', hint: 'Garment care, collected and returned' },
    ],
  },
  { to: '/contact', label: 'Contact' },
];

/** The drawer repeats the same destinations, plus Home and the guest self-service link. */
const DRAWER_SECTIONS: { heading: string | null; links: NavLeaf[] }[] = [
  {
    heading: null,
    links: [
      { to: '/', label: 'Home', end: true },
      { to: '/our-story', label: 'Our Story' },
    ],
  },
  { heading: 'Menu', links: [{ to: '/menu/drinks', label: 'Drinks' }, { to: '/menu/food', label: 'Food' }] },
  {
    heading: 'What’s On',
    links: [
      { to: '/experiences', label: 'Experiences' },
      { to: '/events', label: 'Events' },
      { to: '/gallery', label: 'Gallery' },
    ],
  },
  {
    heading: 'Services',
    links: [
      { to: '/concierge', label: 'Concierge' },
      { to: '/laundry', label: 'Laundry' },
    ],
  },
  {
    heading: null,
    links: [
      { to: '/contact', label: 'Contact' },
      { to: '/manage-reservation', label: 'Manage Reservation' },
    ],
  },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Which group is open, and whether hovering or clicking opened it. Without the second
  // half, hovering a trigger opens the panel and the click that follows closes it again.
  const [open, setOpen] = useState<{ group: string; by: 'hover' | 'click' } | null>(null);
  const openGroup = open?.group ?? null;
  const closeGroup = () => setOpen(null);
  const location = useLocation();
  const isDesktop = useMediaQuery(DESKTOP_NAV_QUERY);
  const navRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelIdBase = useId();

  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the drawer and any open group whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
    setOpen(null);
  }, [location.pathname]);

  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
    else setOpen(null);
  }, [isDesktop]);

  // Escape closes the drawer and returns focus to the toggle that opened it.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // A pointer press anywhere outside the nav closes an open group; Escape returns focus
  // to the trigger so keyboard users are not dropped at the top of the document.
  useEffect(() => {
    if (!openGroup) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) closeGroup();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const trigger = navRef.current?.querySelector<HTMLButtonElement>(
        `[data-group-trigger="${CSS.escape(openGroup)}"]`,
      );
      closeGroup();
      trigger?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openGroup]);

  const groupIsActive = (group: NavGroup) =>
    group.children.some(
      (child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`),
    );

  const solid = scrolled || !isHome;

  return (
    <header
      className="sz-header sz-no-print"
      data-solid={solid}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: solid ? 'rgba(5,3,1,0.96)' : 'linear-gradient(180deg,rgba(0,0,0,0.55),transparent)',
        borderBottom: solid ? '1px solid var(--sz-brown)' : '1px solid transparent',
        backdropFilter: solid ? 'blur(12px)' : undefined,
        WebkitBackdropFilter: solid ? 'blur(12px)' : undefined,
        transition: 'background 300ms ease, border-color 300ms ease',
      }}
    >
      <div className="sz-header__bar">
        <Link to="/" aria-label="SAZERAC — home" className="sz-header__logo">
          <img src={logoWhite} alt="SAZERAC" width={140} height={34} />
        </Link>

        {isDesktop && (
          <nav
            className="sz-header__nav"
            aria-label="Main"
            ref={navRef}
            onBlur={(event) => {
              // Leaving the nav entirely by keyboard closes whatever was open.
              if (!event.currentTarget.contains(event.relatedTarget as Node)) closeGroup();
            }}
          >
            {NAV_ITEMS.map((entry) => {
              if (!isGroup(entry)) {
                return (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.end}
                    className={({ isActive }) => (isActive ? 'sz-navlink sz-navlink--active' : 'sz-navlink')}
                  >
                    {entry.label}
                  </NavLink>
                );
              }

              const isOpen = openGroup === entry.label;
              const panelId = `${panelIdBase}-${entry.label.replace(/\W+/g, '')}`;

              return (
                <div
                  key={entry.label}
                  className="sz-navgroup"
                  onMouseEnter={() => setOpen({ group: entry.label, by: 'hover' })}
                  onMouseLeave={() => setOpen((current) => (current?.group === entry.label ? null : current))}
                >
                  <button
                    type="button"
                    data-group-trigger={entry.label}
                    className={
                      groupIsActive(entry) ? 'sz-navlink sz-navlink--active sz-navgroup__trigger' : 'sz-navlink sz-navgroup__trigger'
                    }
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() =>
                      setOpen((current) =>
                        // Only a second click closes it; a click after hover keeps it open.
                        current?.group === entry.label && current.by === 'click'
                          ? null
                          : { group: entry.label, by: 'click' },
                      )
                    }
                  >
                    {entry.label}
                    <span className="sz-navgroup__caret" aria-hidden="true" data-open={isOpen}>
                      ▾
                    </span>
                  </button>

                  <div id={panelId} className="sz-navgroup__panel" hidden={!isOpen}>
                    {entry.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={({ isActive }) =>
                          isActive ? 'sz-navgroup__link sz-navgroup__link--active' : 'sz-navgroup__link'
                        }
                      >
                        <span className="sz-navgroup__label">{child.label}</span>
                        {child.hint && <span className="sz-navgroup__hint">{child.hint}</span>}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
          </nav>
        )}

        <div className="sz-header__actions">
          {isDesktop && <span className="sz-header__lang">EN / IT</span>}
          <Link to="/reservations" className="sz-btn sz-btn--gold sz-header__cta">
            Reserve a Table
          </Link>
          {!isDesktop && (
            <button
              ref={toggleRef}
              type="button"
              className="sz-header__toggle"
              aria-expanded={menuOpen}
              aria-controls="sz-mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
            </button>
          )}
        </div>
      </div>

      {!isDesktop && menuOpen && (
        <div id="sz-mobile-nav" className="sz-header__drawer">
          <nav aria-label="Mobile">
            {DRAWER_SECTIONS.map((section, index) => (
              <div key={section.heading ?? `section-${index}`} className="sz-drawer-section">
                {section.heading && <p className="sz-drawer-heading">{section.heading}</p>}
                {section.links.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      isActive ? 'sz-drawer-link sz-drawer-link--active' : 'sz-drawer-link'
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
